import { t } from 'i18next';
import React, {
  useEffect,
  useState,
  useRef,
  SetStateAction,
  Dispatch,
} from 'react';
import { GetAffectation } from 'services/affectationService';
import UserShortCut from './UserShortCut';
import { AffectationType, InitialValuesAffectation } from 'components/types';
import { useParams, usePathname } from 'next/navigation';

type AffectationUser = AffectationType['users'][number];

type AffectationTransfer = {
  version: number;
  toAssigned: boolean;
  users: AffectationUser[];
};

const normalizeSearchText = (value: string): string =>
  value.trim().toLowerCase();

const userMatchesSearch = (
  user: AffectationUser,
  searchValue: string,
): boolean => {
  const normalizedSearch = normalizeSearchText(searchValue);
  if (!normalizedSearch) return true;

  return normalizeSearchText(
    `${user.fullName} ${user.email} ${user.rolename} ${user.fonction}`,
  ).includes(normalizedSearch);
};

function AffectationBox({
  title,
  description,
  className,
  url,
  assigend,
  ids,
  setIds,
  update,
  // setUpdate,
  setInitialValues,
  initialValues,
  roleName,
  genrateAvatar = false,
  isFunction = false,
  transfer,
  onSelectedUsersChange,
}: {
  title: string;
  description: string;
  className?: string;
  url: string;
  assigend: boolean;
  ids: string[];
  setIds: Dispatch<SetStateAction<string[]>>;
  update: number;
  setUpdate: Dispatch<SetStateAction<number>>;
  setInitialValues: Dispatch<SetStateAction<InitialValuesAffectation>>;
  initialValues: InitialValuesAffectation;
  roleName?: string;
  genrateAvatar?: boolean;
  isFunction?: boolean;
  transfer?: AffectationTransfer | null;
  onSelectedUsersChange?: (users: AffectationUser[]) => void;
}) {
  const pathName = usePathname();
  const { id } = useParams();
  const [data, setData] = useState<AffectationType | null>(null);
  const [filters, setFilters] = useState<InitialValuesAffectation>({
    searchValue: '',
    length: 20,
    currentList: 1,
    id: pathName.includes('role') ? 0 : (id as string),
    roleName: roleName ?? '',
    userid: '',
    roleId: pathName.includes('role') ? (id as string) : '',
    ids: [],
    assigend: assigend,
    selectAll: false,
    isGroup: true,
  });
  const [prevSearch, setPrevSearch] = useState<string | null>(null);
  const [prevUpdate, setPrevUpdate] = useState<number | null>(0);

  const listRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const appliedTransferRef = useRef<number | null>(null);
  const isSelectAllChecked = initialValues.selectAll && ids.length === 0;
  const isSelectAllIndeterminate = ids.length > 0;

  useEffect(() => {
    if (update !== prevUpdate && prevUpdate !== null) {
      setFilters((prev) => ({ ...prev, currentList: 1 }));
    }
    const shouldAppend =
      filters.searchValue === prevSearch &&
      update === prevUpdate &&
      filters.currentList > 1;

    setPrevSearch(filters.searchValue);
    setPrevUpdate(update);

    const fetchAffectation = async () => {
      try {
        const response = await GetAffectation(url, filters);
        hasMoreRef.current = response.users.length > 0;
        setData((prev) =>
          prev && shouldAppend
            ? { ...prev, users: [...prev.users, ...response.users] }
            : response,
        );
      } finally {
        loadingMoreRef.current = false;
      }
    };
    fetchAffectation();
  }, [filters, update]);

  // Request the next page while ~2 list-heights of users are still below,
  // so it is already rendered by the time the user reaches the bottom.
  const loadMoreIfNearBottom = () => {
    const list = listRef.current;
    if (!list || !data || loadingMoreRef.current || !hasMoreRef.current) return;
    if (data.users.length >= data.totalUsers) return;
    const { scrollTop, scrollHeight, clientHeight } = list;
    if (scrollHeight - scrollTop - clientHeight <= clientHeight * 2) {
      loadingMoreRef.current = true;
      setFilters((prev) => ({ ...prev, currentList: prev.currentList + 1 }));
    }
  };

  useEffect(() => {
    const currentListRef = listRef.current;
    if (currentListRef) {
      currentListRef.addEventListener('scroll', loadMoreIfNearBottom);
    }
    return () => {
      if (currentListRef) {
        currentListRef.removeEventListener('scroll', loadMoreIfNearBottom);
      }
    };
  }, [data]);

  useEffect(() => {
    loadMoreIfNearBottom();
  }, [data]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSelectAllIndeterminate;
      selectAllRef.current.setAttribute(
        'data-indeterminate',
        isSelectAllIndeterminate.toString(),
      );
    }
  }, [isSelectAllIndeterminate]);

  useEffect(() => {
    if (!data || !onSelectedUsersChange) return;

    const selectedUsers = data.users.filter((user) =>
      initialValues.selectAll ? !ids.includes(user.id) : ids.includes(user.id),
    );

    onSelectedUsersChange(selectedUsers);
  }, [data, ids, initialValues.selectAll, onSelectedUsersChange]);

  useEffect(() => {
    if (
      !transfer ||
      transfer.users.length === 0 ||
      appliedTransferRef.current === transfer.version
    ) {
      return;
    }

    appliedTransferRef.current = transfer.version;
    const transferredIds = new Set(transfer.users.map((user) => user.id));
    const isDestination = assigend === transfer.toAssigned;

    setData((prev) => {
      if (!prev) return prev;

      if (isDestination) {
        const existingIds = new Set(prev.users.map((user) => user.id));
        const usersToAdd = transfer.users.filter(
          (user) =>
            !existingIds.has(user.id) &&
            userMatchesSearch(user, filters.searchValue),
        );

        if (usersToAdd.length === 0) return prev;

        return {
          ...prev,
          totalUsers: prev.totalUsers + usersToAdd.length,
          users: [...usersToAdd, ...prev.users],
        };
      }

      const users = prev.users.filter((user) => !transferredIds.has(user.id));
      const removedCount = prev.users.length - users.length;
      if (removedCount === 0) return prev;

      return {
        ...prev,
        totalUsers: Math.max(0, prev.totalUsers - removedCount),
        users,
      };
    });
  }, [assigend, filters.searchValue, transfer]);

  const handleSearch = (e: string) => {
    setFilters((prev) => ({ ...prev, searchValue: e, currentList: 1 }));
  };
  const handleSelectUser = (userId: string) => {
    setIds((prev: string[]) => {
      const updatedIds = prev.includes(userId)
        ? prev.filter((id: string) => id !== userId)
        : [...prev, userId];

      return updatedIds;
    });
  };

  const getTwoLetter = (name: string): string => {
    const words = name.split(' ');
    if (words.length > 1) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      return (words[0][0] + words[0][1]).toUpperCase();
    }
  };

  return (
    <>
      {data && (
        <div className={`col p-0 minthr-p-0 ${className}`}>
          <div className="col p-0">
            <div className="minthr-bg-light-gray1 minthr-border-rounded minthr-p-4">
              <div className="minthr-flex minthr-flex-space-between minthr-mb-4">
                <h4 className="minthr-mr-auto">{title}</h4>
                <span className="h5 inline-employs-counter">
                  <span className="minthr-color-primary user-total">
                    {data.totalUsers}{' '}
                  </span>
                  <span className="minthr-color-gray">
                    {t(isFunction ? 'Fonction' : 'constent.workers') +
                      ' /' +
                      data.allUsers}
                  </span>
                </span>
                <img
                  className="minthr-icon-m minthr-icon-gray minthr-ml-2"
                  src="/icons/user.svg"
                  alt=""
                />
              </div>

              <div className="minthr-searchbar minthr-searchbar-s minthr-mb-4 search-input">
                <input
                  className="w-100"
                  name="search"
                  placeholder={t('collaborateur.search')}
                  onChange={(e) => handleSearch(e.currentTarget.value)}
                />
                <span className="search-icon"></span>
              </div>

              <div className="minthr-flex minthr-flex-items-center minthr-flex-justify-end minthr-mb-4 minthr-pr-6">
                <span className="minthr-color-primary minthr-mr-3">
                  {t('constent.selectAll')}
                </span>
                <div className="minthr-chackbox">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    id="checkAll"
                    className="minthr-form-control"
                    checked={isSelectAllChecked}
                    readOnly
                  />
                  <label
                    className="minthr-form-label minthr-text-hide"
                    onClick={() => {
                      setIds([]);
                      setInitialValues((prev: InitialValuesAffectation) => ({
                        ...prev,
                        selectAll: !prev.selectAll,
                      }));
                    }}
                  >
                    {t('constent.selectAll')}
                  </label>
                </div>
              </div>

              <div
                ref={listRef}
                className="minthr-collab-non-assigned minthr-box minthr-box-round minthr-py-4 minthr-px-6 minthr-overflow-auto"
              >
                <div className="newUser">
                  {data.users.map((user) => (
                    <div
                      key={user.id}
                      className="minthr-flex minthr-flex-items-center minthr-mb-3 user-scrol"
                    >
                      {genrateAvatar ? (
                        <div
                          className="minthr-avatar minthr-avatar-m minthr-mr-2 minthr-bg-secondary-opacity minthr-color-primary"
                          style={{ cursor: 'pointer' }}
                        >
                          {getTwoLetter(user.fullName)}
                        </div>
                      ) : (
                        <UserShortCut
                          className="minthr-avatar minthr-avatar-m minthr-mr-2"
                          src={`upload/profileimages/${user.id}/${user.picturePath}`}
                          alt=""
                        />
                      )}

                      <div>
                        <h5>{user.fullName}</h5>
                        <span className="minthr-icon-text">
                          {user.rolename}
                        </span>
                      </div>

                      <div className="minthr-chackbox minthr-ml-auto">
                        <input
                          type="checkbox"
                          id={`user-${user.id}`}
                          name="user_id"
                          className="minthr-form-control"
                          checked={
                            (ids.includes(user.id) &&
                              !initialValues.selectAll) ||
                            (initialValues.selectAll && !ids.includes(user.id))
                          }
                          readOnly
                        />
                        <label
                          className="minthr-form-label minthr-text-hide"
                          onClick={() => handleSelectUser(user.id)}
                        >
                          {user.fullName}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="minthr-flex minthr-mt-4 text-s minthr-color-gray">
                <img
                  className="minthr-icon-m minthr-icon-gray minthr-mr-2 minthr-icon-info"
                  src="/icons/info.svg"
                  alt=""
                />
                {description}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AffectationBox;
